import { Fragment, useState } from "react";
import {
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Spinner,
} from "reactstrap";
import { useForm, Controller } from "react-hook-form";
import { Breadcrumbs } from "../../../AbstractElements";
import HeaderCard from "../../../Components/Common/Component/HeaderCard";
import { useNavigate } from "react-router-dom";
import { useDataFetch } from "../../Hooks/Use-data-fetch";
import { toast } from "react-toastify";

export const UserCreate = () => {
  const navigate = useNavigate();
  const [emailConflict, setEmailConflict] = useState(null);

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
    setError,
  } = useForm({
    defaultValues: {
      referral_code: "",
      name: "",
      email: "",
      phone: "",
      document: "",
      country_id: "",
      send_email: true,
    },
  });

  const referralCode = watch("referral_code");
  const { getData, postData } = useDataFetch();

  const { data: countriesData, isLoading: isLoadingCountries } = getData(
    ["countries"],
    "/product/all/countries",
    { enabled: true }
  );

  const countries = countriesData?.data?.countries || [];

  const {
    data: referralData,
    error: referralError,
    refetch: refetchReferral,
  } = getData(
    [`referralCode-${referralCode}`],
    referralCode ? `/users/get/multyhealth_code/${referralCode}` : "",
    { enabled: false }
  );

  const { mutateAsync: createUser, isPending } = postData("/users");

  const handleReferralBlur = () => {
    if (referralCode) {
      refetchReferral();
    }
  };

  const onSubmit = async (data) => {
    const payload = {
      email: data.email,
      name: data.name,
      phone: data.phone || null,
      document: data.document,
      reference_id: data.referral_code,
      country_id: data.country_id,
      send_email: data.send_email,
    };

    setEmailConflict(null);

    try {
      await createUser(payload);
      toast.success("Cliente creado exitosamente");
      navigate("/user");
    } catch (error) {
      toast.error("El correo ya está en uso");
    }
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Usuarios" title="Registrar Cliente" mainTitle="Registrar Cliente" />
      <Container fluid>
        <Row>
          <Col sm="12">
            <Card>
              <HeaderCard title="Formulario de Registro de Cliente" />
              <CardBody>
                <Form onSubmit={handleSubmit(onSubmit)}>
                  {/* Código de referido */}
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label for="referral_code">Código de referido</Label>
                        <Controller
                          name="referral_code"
                          control={control}
                          rules={{ required: "Este campo es obligatorio" }}
                          render={({ field }) => (
                            <Input
                              {...field}
                              type="text"
                              id="referral_code"
                              onBlur={handleReferralBlur}
                              placeholder="Código de referido"
                            />
                          )}
                        />
                        {errors.referral_code && (
                          <span className="text-danger small">{errors.referral_code.message}</span>
                        )}
                        {referralError && <div className="text-danger">Código inválido</div>}
                        {referralData?.data?.user && (
                          <div className="text-success">
                            Referido por: <strong>{referralData.data.user.name}</strong>
                            <br />
                            Correo: <small>{referralData.data.user.email}</small>
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Selector de país */}
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label className="fw-bold">País</Label>
                        <Controller
                          name="country_id"
                          control={control}
                          rules={{ required: "El país es obligatorio" }}
                          render={({ field }) => (
                            <Input
                              type="select"
                              {...field}
                              onChange={(e) => {
                                const selected = countries.find(
                                  (c) => c.id === e.target.value
                                );
                                if (selected) field.onChange(selected.id);
                              }}
                              disabled={isLoadingCountries}
                            >
                              <option value="">Selecciona un país</option>
                              {isLoadingCountries ? (
                                <option>Cargando...</option>
                              ) : (
                                countries.map((country) => (
                                  <option key={country.id} value={country.id}>
                                    {country.name}
                                  </option>
                                ))
                              )}
                            </Input>
                          )}
                        />
                        {errors.country_id && (
                          <span className="text-danger small">{errors.country_id.message}</span>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Documento y nombre */}
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Documento</Label>
                        <Controller
                          name="document"
                          control={control}
                          rules={{ required: "El documento es obligatorio" }}
                          render={({ field }) => (
                            <Input {...field} type="text" placeholder="Número de documento" />
                          )}
                        />
                        {errors.document && (
                          <span className="text-danger small">{errors.document.message}</span>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Nombre</Label>
                        <Controller
                          name="name"
                          control={control}
                          rules={{ required: "El nombre es obligatorio" }}
                          render={({ field }) => (
                            <Input {...field} type="text" placeholder="Nombre completo" />
                          )}
                        />
                        {errors.name && (
                          <span className="text-danger small">{errors.name.message}</span>
                        )}
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Correo, teléfono y checkbox */}
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>Correo</Label>
                        <Controller
                          name="email"
                          control={control}
                          rules={{ required: "El correo es obligatorio" }}
                          render={({ field }) => (
                            <Input {...field} type="email" placeholder="ejemplo@correo.com" />
                          )}
                        />
                        {errors.email && (
                          <span className="text-danger small">{errors.email.message}</span>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>Teléfono</Label>
                        <Controller
                          name="phone"
                          control={control}
                          render={({ field }) => (
                            <Input {...field} type="text" placeholder="Opcional" />
                          )}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
            
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label className="fw-bold d-block">Notificación por correo</Label>
                        <Controller
                          name="send_email"
                          control={control}
                          render={({ field }) => (
                            <div className="m-t-15 m-checkbox-inline custom-radio-ml">
                              <div className="checkbox checkbox-dark">
                                <Input
                                  id="send_email"
                                  type="checkbox"
                                  checked={!!field.value}
                                  onChange={(e) => field.onChange(e.target.checked)}
                                />
                                <Label htmlFor="send_email" className="fw-normal">
                                  Enviar correo de registro al cliente
                                </Label>
                              </div>
                            </div>
                          )}
                        />
                      </FormGroup>
                    </Col>
                  </Row>


                  {/* Botón de envío */}
                  <Button color="primary" type="submit" disabled={isPending}>
                    {isPending ? <Spinner size="sm" /> : "Guardar Cliente"}
                  </Button>
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};
